import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Zap, Plus, LayoutGrid, LogOut, Shield } from "lucide-react";
import { Logo } from "@/components/Logo";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export const Header = () => {
  const { user, wallet, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/70 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="nav-brand-logo" className="flex items-center">
          <Logo size={32} />
        </Link>

        <nav className="hidden items-center gap-7 md:flex">
          <Link to="/create" data-testid="nav-create" className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">Create</Link>
          <Link to="/billing" data-testid="nav-pricing" className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">Pricing</Link>
          <Link to="/gallery" data-testid="nav-examples" className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">Gallery</Link>
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <>
              <Link to="/billing" data-testid="user-credit-balance" className="hidden items-center gap-1.5 rounded-full border border-border bg-secondary/60 px-3 py-1.5 text-sm font-semibold sm:flex">
                <span className="inline-block h-2 w-2 rounded-full bg-accent shadow-[0_0_8px_theme(colors.cyan.400)]" style={{ boxShadow: "0 0 8px #19F5D2", background: "#19F5D2" }} />
                {wallet.available_balance}
                <span className="text-muted-foreground font-normal">credits</span>
              </Link>
              <Button size="sm" variant="outline" data-testid="buy-credits-button" onClick={() => navigate("/billing")} className="hidden rounded-full sm:inline-flex">
                <Plus className="mr-1 h-4 w-4" /> Buy
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-trigger" className="rounded-full outline-none ring-primary focus-visible:ring-2">
                    <Avatar className="h-9 w-9 border border-border">
                      <AvatarImage src={user.picture} />
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold">{(user.name || "U")[0].toUpperCase()}</AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-semibold truncate">{user.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/dashboard")}><LayoutGrid className="mr-2 h-4 w-4" /> Dashboard</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-library" onClick={() => navigate("/library")}><LayoutGrid className="mr-2 h-4 w-4" /> My Creations</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-billing" onClick={() => navigate("/billing")}><Zap className="mr-2 h-4 w-4" /> Credits & Billing</DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}><Shield className="mr-2 h-4 w-4" /> Admin Panel</DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}><LogOut className="mr-2 h-4 w-4" /> Log out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" data-testid="nav-login" onClick={() => navigate("/login")}>Login</Button>
              <Button size="sm" data-testid="nav-start-creating" onClick={() => navigate("/login")} className="rounded-full">Start Creating</Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};
